package io.horion.cv.components.dynamicfields

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import com.varabyte.kobweb.silk.style.toModifier
import io.horion.cv.components.widgets.InfoBox
import io.horion.cv.components.widgets.TextFormField
import io.horion.cv.styles.DynamicFieldsContainerStyle
import io.horion.cv.viewModel.CvViewModel
import org.jetbrains.compose.web.css.px

@Composable
fun DynamicFieldsSection(
    fields: List<DynamicField>,
    cvViewModel: CvViewModel,
) {
    var openedFields by remember { mutableStateOf<Set<String>>(emptySet()) }
    val cvUiState = cvViewModel.cvUiState
    val pi = cvUiState.data.personalInformation

    LaunchedEffect(pi) {
        val fieldsWithData = fields.filter { field ->
            val value = when (field.id) {
                "permis" -> pi.drivingLicense
                "etat" -> pi.maritalStatus
                "nat" -> pi.nationality
                "lieu" -> pi.country
                "adresse" -> pi.address
                "linkedin" -> pi.links.getOrNull(0)?.second
                "site" -> pi.siteWeb?.second
                else -> null
            }
            !value.isNullOrBlank()
        }.map { it.id }.toSet()

        openedFields = openedFields + fieldsWithData
    }

    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(20.px)
    ) {
        openedFields.forEach { fieldId ->
            val field = fields.find { it.id == fieldId } ?: return@forEach

            val fieldValue = when (field.id) {
                "permis" -> pi.drivingLicense
                "etat" -> pi.maritalStatus
                "nat" -> pi.nationality
                "lieu" -> pi.country
                "adresse" -> pi.address
                "linkedin" -> pi.links.getOrNull(0)?.second ?: ""
                "site" -> pi.siteWeb?.second ?: ""
                else -> ""
            }

            TextFormField(
                label = field.label,
                value = fieldValue,
                onValueChange = { newValue ->
                    cvViewModel.updatePersonalInfo { currentPi ->
                        when (field.id) {
                            "permis" -> currentPi.copy(drivingLicense = newValue)
                            "etat" -> currentPi.copy(maritalStatus = newValue)
                            "nat" -> currentPi.copy(nationality = newValue)
                            "lieu" -> currentPi.copy(country = newValue)
                            "adresse" -> currentPi.copy(address = newValue)
                            "linkedin" -> {
                                val list = currentPi.links.toMutableList()
                                if (list.isEmpty()) list.add("" to newValue)
                                else list[0] = list[0].copy(second = newValue)
                                currentPi.copy(links = list)
                            }
                            "site" -> currentPi.copy(siteWeb = newValue to newValue)
                            else -> currentPi
                        }
                    }
                },
                modifier = Modifier.fillMaxWidth(),
            )
        }
    }

    Spacer()

    val availableFields = fields.filterNot { openedFields.contains(it.id) }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .displayUntil(Breakpoint.MD)
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(10.px)
        ) {
            availableFields.chunked(4).forEach { rowFields ->
                Row(
                    modifier = DynamicFieldsContainerStyle.toModifier(),
                    horizontalArrangement = Arrangement.spacedBy(10.px)
                ) {
                    rowFields.forEach { field ->
                        InfoBox(
                            text = field.label,
                            onClick = { openedFields = openedFields + field.id },
                        )
                    }
                }
            }
        }
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .displayIfAtLeast(Breakpoint.MD)
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(10.px)
        ) {
            availableFields.chunked(4).forEach { rowFields ->
                Row(
                    modifier = DynamicFieldsContainerStyle.toModifier(),
                    horizontalArrangement = Arrangement.spacedBy(10.px)
                ) {
                    rowFields.forEach { field ->
                        InfoBox(
                            text = field.label,
                            width = field.collapsedWidth,
                            onClick = { openedFields = openedFields + field.id },
                        )
                    }
                }
            }
        }
    }
}